import type { ReactNode } from 'react';
import type { Lcert } from '@/types';

export type LcertCheckField =
  | 'pvReviewed'
  | 'pvTrainingCompleted'
  | 'pvTrainedStaffAvailable'
  | 'pvEquipmentChecked'
  | 'pvNonEvaluatedMedicalCompleted'
  | 'pvDosimetryTestedInspected';

export type LcertNotesField =
  | 'pvReviewedNotes'
  | 'pvTrainingCompletedNotes'
  | 'pvTrainedStaffAvailableNotes'
  | 'pvEquipmentCheckedNotes'
  | 'pvNonEvaluatedMedicalNotes'
  | 'pvDosimetryTestedInspectedNotes';

export type LcertFilesField =
  'pvReviewedFiles' | 'pvTrainingCompletedFiles' | 'pvNonEvaluatedMedicalFiles';

export interface LcertSection {
  key: string;
  title: string;
  checkField: LcertCheckField;
  notesField: LcertNotesField;
  /** Checkbox label (the certification statement). */
  text: string;
  /** Guidance shown under the statement (hidden once a letter is approved). */
  note?: ReactNode;
  /** Attachment group: suffix appended to the letter's pvGlobalID, the flag column, and the files title. */
  files?: { suffix: string; flagField: LcertFilesField; title: string };
  /** Only rendered when the letter was seeded as a non-evaluated medical service drill county. */
  onlyWhen?: (letter: Lcert) => boolean;
}

const RecordsNote = ({ last }: { last: string }) => (
  <>
    <b>Note:</b> Please maintain a copy of records for review during FEMA SAV, including:
    <ul className='rep:mt-1 rep:list-disc rep:pl-5'>
      <li>Type of equipment</li>
      <li>Quantity of equipment</li>
      <li>Location of equipment</li>
      <li>Calibration frequency</li>
      <li>{last}</li>
    </ul>
  </>
);

/** The six certification sections, in page order (text ported verbatim from the legacy form). */
export const LCERT_SECTIONS: LcertSection[] = [
  {
    key: 'plans',
    title: 'Radiological plans',
    checkField: 'pvReviewed',
    notesField: 'pvReviewedNotes',
    text: 'Radiological plans, a cross-reference between corresponding evaluation criteria (i.e. crosswalk), SOPs, and associated Letters of Agreement were reviewed this year for accuracy and completeness.',
    note: (
      <>
        <b>Note:</b> Please upload copies of all changes below
      </>
    ),
    files: { suffix: '-reviewed', flagField: 'pvReviewedFiles', title: 'Plans Report' },
  },
  {
    key: 'training',
    title: 'Emergency response training',
    checkField: 'pvTrainingCompleted',
    notesField: 'pvTrainingCompletedNotes',
    text: 'Annual radiological emergency response training was accomplished this year.',
    note: (
      <>
        <b>Note:</b> Please upload copies of completed training rosters
      </>
    ),
    files: {
      suffix: '-training',
      flagField: 'pvTrainingCompletedFiles',
      title: 'Response',
    },
  },
  {
    key: 'staffing',
    title: 'Protracted activation staffing',
    checkField: 'pvTrainedStaffAvailable',
    notesField: 'pvTrainedStaffAvailableNotes',
    text: 'Sufficiently trained and capable staff is available to maintain 24-hour capability for protracted activation.',
    note: (
      <>
        <b>Note:</b> Please maintain a copy of 24-hour staffing roster for review during FEMA staff
        assistance visit (SAV).
      </>
    ),
  },
  {
    key: 'instruments',
    title: 'Radiological survey instrument checks',
    checkField: 'pvEquipmentChecked',
    notesField: 'pvEquipmentCheckedNotes',
    text: 'Inspection, inventory, and operational checks were made of survey instruments used for radiological monitoring at least once each calendar quarter and upon each use.',
    note: <RecordsNote last='Dates of inspection/inventory check' />,
  },
  {
    key: 'drill',
    title: 'Non-evaluated medical service drill',
    checkField: 'pvNonEvaluatedMedicalCompleted',
    notesField: 'pvNonEvaluatedMedicalNotes',
    text: 'Off-year non-evaluated medical services drill was accomplished this year',
    files: {
      suffix: '-service',
      flagField: 'pvNonEvaluatedMedicalFiles',
      title: 'Service Drill Report',
    },
    onlyWhen: (letter) => !!letter.pvIsNonEvaluatedMedical,
  },
  {
    key: 'dosimetry',
    title: 'Dosimetry test',
    checkField: 'pvDosimetryTestedInspected',
    notesField: 'pvDosimetryTestedInspectedNotes',
    text: 'Direct-reading dosimetry has been tested for accuracy and inspected for leakage this year.',
    note: <RecordsNote last='Dates of calibration' />,
  },
];

/** Reasons offered by the state's "Request changes" dialog (stored as "-<reason>" lines in pvReason). */
export const CHANGE_REASONS = [
  'Radiological Plans',
  'Emergency Response Training',
  // Legacy label (sic) - it is what gets stored in pvReason.
  'Protected Activation Staffing',
  'Radiological Survey Instruments Check',
  'Non-evaluated Medical Service Drill',
  'Dosimetry Tests',
];

/** Splits the stored pvReason into its comment and reason lines. */
export const parseReason = (pvReason?: string | null): { comment: string; reasons: string[] } => {
  const lines = String(pvReason || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return {
    comment: lines.filter((l) => !l.startsWith('-')).join('\n'),
    reasons: lines.filter((l) => l.startsWith('-')).map((l) => l.replace(/^-\s*/, '')),
  };
};

/** Available years for "Create new year": [Y-1, Y, Y+1] when unused, then Y+2..Y+4 always. */
export const availableYears = (used: (string | number)[]): string[] => {
  const usedSet = new Set(used.map(String));
  const y = new Date().getFullYear();
  const out: string[] = [];
  for (const candidate of [y - 1, y, y + 1])
    if (!usedSet.has(String(candidate))) out.push(String(candidate));
  out.push(String(y + 2), String(y + 3), String(y + 4));
  return out;
};
